<template>
  <div class="order-management-wrapper">
    <div v-if="isFormVisible" class="order-form-container">
      <div class="form-header">
        <h2>{{ isEditing ? '編輯訂單' : '新增訂單' }}</h2>
        <button class="close-btn" @click="handleFormCancelled">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <form @submit.prevent="handleSubmit" class="order-form">
        <div class="form-group">
          <label>客戶姓名 <span class="required">*</span></label>
          <input v-model="formData.customerName" type="text" class="form-input" required />
        </div>

        <div class="form-group">
          <label>訂單狀態 <span class="required">*</span></label>
          <input :value="getStatusText(formData.orderStatus)" type="text" class="form-input" disabled />
        </div>

        <div class="form-group">
          <label>付款方式</label>
          <select v-model="formData.paymentMethod" class="form-input">
            <option value="">請選擇</option>
            <option value="ATM">ATM轉帳</option>
            <option value="CONVENIENCE_STORE">超商代碼</option>
          </select>
        </div>

        <div class="form-group">
          <label>金流平台</label>
          <select v-model="formData.paymentGateway" class="form-input">
            <option value="NEWEBPAY">藍新金流</option>
          </select>
        </div>

        <div v-if="formData.paymentMethod === 'ATM'" class="form-group">
          <label>匯款銀行代碼 <span class="required">*</span></label>
          <select v-model="formData.sourceBankId" class="form-input" required>
            <option value="">請選擇銀行</option>
            <option value="004">004 臺灣銀行</option>
            <option value="005">005 臺灣土地銀行</option>
            <option value="006">006 合作金庫商業銀行</option>
            <option value="007">007 第一商業銀行</option>
            <option value="008">008 華南商業銀行</option>
            <option value="009">009 彰化商業銀行</option>
            <option value="011">011 上海商業儲蓄銀行</option>
            <option value="012">012 台北富邦商業銀行</option>
            <option value="013">013 國泰世華商業銀行</option>
            <option value="016">016 高雄銀行</option>
            <option value="017">017 兆豐國際商業銀行</option>
            <option value="021">021 花旗(台灣)商業銀行</option>
            <option value="050">050 臺灣中小企業銀行</option>
            <option value="052">052 渣打國際商業銀行</option>
            <option value="053">053 台中商業銀行</option>
            <option value="054">054 京城商業銀行</option>
            <option value="081">081 匯豐(台灣)商業銀行</option>
            <option value="101">101 瑞興商業銀行</option>
            <option value="102">102 華泰商業銀行</option>
            <option value="103">103 臺灣新光商業銀行</option>
            <option value="108">108 陽信商業銀行</option>
            <option value="118">118 板信商業銀行</option>
            <option value="147">147 三信商業銀行</option>
            <option value="700">700 中華郵政股份有限公司</option>
            <option value="803">803 聯邦商業銀行</option>
            <option value="805">805 遠東國際商業銀行</option>
            <option value="806">806 元大商業銀行</option>
            <option value="807">807 永豐商業銀行</option>
            <option value="808">808 玉山商業銀行</option>
            <option value="809">809 凱基商業銀行</option>
            <option value="810">810 星展(台灣)商業銀行</option>
            <option value="812">812 台新國際商業銀行</option>
            <option value="814">814 大眾商業銀行</option>
            <option value="815">815 日盛國際商業銀行</option>
            <option value="816">816 安泰商業銀行</option>
            <option value="822">822 中國信託商業銀行</option>
            <option value="823">823 將來商業銀行</option>
            <option value="824">824 連線商業銀行</option>
            <option value="826">826 樂天國際商業銀行</option>
          </select>
        </div>

        <div v-if="formData.paymentMethod === 'ATM'" class="form-group">
          <label>匯款銀行帳號 <span class="required">*</span></label>
          <input v-model="formData.sourceAccountNo" type="text" class="form-input" required placeholder="請輸入完整帳號" />
        </div>

        <div class="form-group">
          <label>發票載具（選填）</label>
          <select v-model="formData.carrierType" class="form-input" @change="formData.carrierId = ''">
            <option value="">不使用載具</option>
            <option value="3J0002">手機條碼載具</option>
          </select>
        </div>

        <div v-if="formData.carrierType" class="form-group">
          <label>手機條碼 <span class="required">*</span></label>
          <input
            v-model.trim="formData.carrierId"
            type="text"
            class="form-input"
            required
            placeholder="例：/A012C3V（/ 開頭共 8 碼）"
          />
        </div>

        <div class="order-items-section">
          <div class="section-header">
            <h3>訂單明細 <span class="required">*</span></h3>
            <div class="header-actions">
              <SystemEmailDisplay />
              <button type="button" class="add-item-btn" @click="addOrderItem">
                <i class="fas fa-plus"></i> 新增品項
              </button>
            </div>
          </div>

          <div v-for="(item, index) in formData.orderItems" :key="index" class="order-item">
            <div class="item-header">
              <span>品項 #{{ index + 1 }}</span>
              <button type="button" class="remove-item-btn" @click="removeOrderItem(index)" v-if="formData.orderItems.length > 1">
                <i class="fas fa-trash"></i>
              </button>
            </div>
            <div class="item-fields">
              <div class="form-group">
                <label>商品名稱 <span class="required">*</span></label>
                <input v-model="item.itemName" type="text" class="form-input" required />
              </div>
              <div class="form-group">
                <label>商品描述</label>
                <input v-model="item.itemDescription" type="text" class="form-input" />
              </div>
              <div class="form-group">
                <label>單價 <span class="required">*</span></label>
                <input v-model.number="item.unitPrice" type="number" step="0.01" min="0.01" class="form-input" required />
              </div>
              <div class="form-group">
                <label>數量 <span class="required">*</span></label>
                <input v-model.number="item.quantity" type="number" min="1" class="form-input" required />
              </div>
              <div class="form-group">
                <label>小計</label>
                <input :value="(item.unitPrice * item.quantity || 0).toFixed(2)" type="text" class="form-input" disabled />
              </div>
            </div>
          </div>
        </div>

        <div class="total-amount">
          <strong>訂單總金額：NT$ {{ calculateTotal.toFixed(2) }}</strong>
        </div>

        <div class="form-actions">
          <button type="submit" class="submit-btn" :disabled="isSaving">
            <i class="fas fa-save"></i> {{ isSaving ? '儲存中...' : '儲存' }}
          </button>
          <button type="button" class="cancel-btn" @click="handleFormCancelled" :disabled="isSaving">
            <i class="fas fa-times"></i> 取消
          </button>
        </div>
      </form>
    </div>

    <div v-else class="order-list-view">
      <div class="page-breadcrumb">
        <span>訂單管理</span> <i class="fas fa-chevron-right separator"></i> <span>訂單列表</span>
      </div>

      <div class="filter-bar">
        <input type="text" v-model="searchConvenienceStoreCode" placeholder="超商代碼..." class="form-input search-input" @keyup.enter="applyFiltersAndResetPage"/>
        <select v-model="searchOrderStatus" class="form-input search-select" @change="applyFiltersAndResetPage">
          <option value="">所有狀態</option>
          <option value="PENDING">尚未取號</option>
          <option value="AWAITING_PAYMENT">等待付款</option>
          <option value="PAID">已付款</option>
          <option value="PAYMENT_FAILED">付款失敗</option>
          <option value="SHIPPED">已出貨</option>
          <option value="REFUNDED">已退款</option>
        </select>
        <button class="filter-btn" @click="applyFiltersAndResetPage">
          <i class="fas fa-search"></i> 搜尋
        </button>
        <button class="clear-filter-btn" @click="clearFiltersAndFetch">
          <i class="fas fa-times"></i> 清除
        </button>
      </div>

      <div class="actions-bar">
        <button class="add-order-btn" @click="openOrderForm()">
          <i class="fas fa-plus-circle"></i> 新增訂單
        </button>
      </div>

      <div v-if="isLoading" class="loading-spinner">
        <p>載入訂單中...</p>
      </div>

      <div v-if="!isLoading && orders.length > 0" class="order-table-container">
        <table class="order-table">
          <thead>
            <tr>
              <th>訂單編號</th>
              <th>超商繳費代碼</th>
              <th>銀行帳號</th>
              <th>訂單日期</th>
              <th>訂單狀態</th>
              <th>付款方式</th>
              <th>總金額</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in orders" :key="order.id">
              <td>{{ order.orderNumber }}</td>
              <td>{{ order.convenienceStoreCode || '-' }}</td>
              <td>{{ order.bankAccount || '-' }}</td>
              <td>{{ order.orderDate }}</td>
              <td>
                <span :class="['status-badge', getStatusClass(order.orderStatus)]">
                  {{ getStatusText(order.orderStatus) }}
                </span>
              </td>
              <td>{{ getPaymentMethodText(order.paymentMethod) }}</td>
              <td>NT$ {{ order.totalAmount?.toFixed(2) || '0.00' }}</td>
              <td class="action-cell">
                <button class="action-btn view-btn" @click="viewOrderDetails(order.id)">
                  <i class="fas fa-eye"></i> 查看
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && orders.length === 0" class="no-data-message">
        目前沒有訂單資料。
      </div>

      <div class="pagination-controls" v-if="!isLoading && totalItems > 0">
        <div class="items-per-page-selector">
          <label for="itemsPerPageSelect">每頁顯示：</label>
          <select id="itemsPerPageSelect" v-model.number="itemsPerPage" @change="handleItemsPerPageChange" class="select-dropdown">
            <option v-for="option in perPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
          <span class="total-items-info"> (共 {{ totalItems }} 項)</span>
        </div>
        <div class="page-navigation">
          <button @click="goToPage(1)" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-left"></i>
          </button>
          <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 頁</span>
          <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-right"></i>
          </button>
          <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useToast } from 'vue-toastification';
import Swal from '@/utils/chineseSwal';
import {
  createOrder,
  getOrderById,
  queryOrders,
  getPendingPaymentOrder,
  createInvoiceManually
} from '@/api/order.api.js';
import { pickRandomDefaultCarrier } from '@/api/defaultCarrier.api.js';
import SystemEmailDisplay from '@/components/SystemEmailDisplay.vue';

const toast = useToast();

const orders = ref([]);
const isLoading = ref(true);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const perPageOptions = ref([10, 20, 30, 100]);
const totalPages = ref(0);
const totalItems = ref(0);
const searchConvenienceStoreCode = ref('');
const searchOrderStatus = ref('');

const isFormVisible = ref(false);
const isEditing = ref(false);
const editingOrderId = ref(null);
const isSaving = ref(false);

const formData = ref({
  customerName: '',
  orderStatus: 'PENDING',
  paymentMethod: '',
  paymentGateway: 'NEWEBPAY',
  sourceBankId: '',
  sourceAccountNo: '',
  carrierType: '',
  carrierId: '',
  orderItems: [
    {
      itemName: '',
      itemDescription: '',
      unitPrice: 0,
      quantity: 1
    }
  ]
});

const validateCarrier = () => {
  const type = formData.value.carrierType;
  if (!type) return true;

  const id = (formData.value.carrierId || '').trim();
  if (!id) {
    toast.error('請輸入手機條碼');
    return false;
  }

  if (!/^\/[0-9A-Z.+-]{7}$/.test(id.toUpperCase())) {
    toast.error('手機條碼格式錯誤，需為「/」開頭共 8 碼，例：/A012C3V');
    return false;
  }
  return true;
};

const calculateTotal = computed(() => {
  return formData.value.orderItems.reduce((sum, item) => {
    return sum + (item.unitPrice * item.quantity || 0);
  }, 0);
});

const fetchOrders = async () => {
  isLoading.value = true;
  const queryParams = {
    page: currentPage.value - 1,
    size: itemsPerPage.value,
    convenienceStoreCode: searchConvenienceStoreCode.value.trim() || undefined,
    orderStatus: searchOrderStatus.value || undefined,
  };
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined) {
      delete queryParams[key];
    }
  });

  try {
    const response = await queryOrders(queryParams);
    orders.value = response.content;
    totalItems.value = response.totalElements;
    totalPages.value = response.totalPages;
  } catch (error) {
    console.error("獲取訂單失敗:", error);
    toast.error('獲取訂單資料失敗，請稍後再試。');
    orders.value = [];
    totalItems.value = 0;
    totalPages.value = 0;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchOrders();
});

watch([currentPage, itemsPerPage], () => {
  if (!isFormVisible.value) {
    fetchOrders();
  }
});

const applyFiltersAndResetPage = () => {
  if (currentPage.value === 1) {
    fetchOrders();
  } else {
    currentPage.value = 1;
  }
};

const clearFiltersAndFetch = () => {
  searchConvenienceStoreCode.value = '';
  searchOrderStatus.value = '';
  applyFiltersAndResetPage();
};

const openOrderForm = async (orderId = null) => {
  if (orderId) {
    try {
      const order = await getOrderById(orderId);
      isEditing.value = true;
      editingOrderId.value = orderId;
      formData.value = {
        customerName: order.customerName,
        orderStatus: order.orderStatus,
        paymentMethod: order.paymentMethod || '',
        paymentGateway: order.paymentGateway || '',
        sourceBankId: order.sourceBankId || '',
        sourceAccountNo: order.sourceAccountNo || '',
        carrierType: order.carrierType || '',
        carrierId: order.carrierId || '',
        orderItems: order.orderItems.map(item => ({
          itemName: item.itemName,
          itemDescription: item.itemDescription || '',
          unitPrice: item.unitPrice,
          quantity: item.quantity
        }))
      };
    } catch (error) {
      console.error("獲取訂單失敗:", error);
      toast.error('獲取訂單資料失敗');
      return;
    }
  } else {
    isEditing.value = false;
    editingOrderId.value = null;

    let carrierType = '';
    let carrierId = '';
    try {
      const picked = await pickRandomDefaultCarrier();
      if (picked && picked.carrierId) {
        carrierType = picked.carrierType || '3J0002';
        carrierId = picked.carrierId;
      }
    } catch (error) {
      console.error('隨機挑選預設載具失敗:', error);
    }

    formData.value = {
      customerName: '客人',
      orderStatus: 'PENDING',
      paymentMethod: 'CONVENIENCE_STORE',
      paymentGateway: 'NEWEBPAY',
      sourceBankId: '',
      sourceAccountNo: '',
      carrierType,
      carrierId,
      orderItems: [
        {
          itemName: '代儲',
          itemDescription: '代儲',
          unitPrice: 0,
          quantity: 1
        }
      ]
    };
  }
  isFormVisible.value = true;
};


const handleSubmit = async () => {
  if (formData.value.orderItems.length === 0) {
    toast.warning('請至少新增一個訂單品項');
    return;
  }

  if (!formData.value.paymentMethod) {
    toast.warning('請選擇付款方式');
    return;
  }

  if (!validateCarrier()) {
    return;
  }

  // ATM轉帳驗證
  if (formData.value.paymentMethod === 'ATM') {
    if (!formData.value.sourceBankId || !formData.value.sourceBankId.trim()) {
      toast.error('請輸入匯款銀行代碼');
      return;
    }
    if (!formData.value.sourceAccountNo || !formData.value.sourceAccountNo.trim()) {
      toast.error('請輸入匯款銀行帳號');
      return;
    }
  }

  // 超商代碼繳費金額驗證
  if (formData.value.paymentMethod === 'CONVENIENCE_STORE') {
    const totalAmount = calculateTotal.value;
    if (totalAmount < 30) {
      toast.error('超商代碼繳費金額不可小於 NT$ 30');
      return;
    }
    if (totalAmount > 20000) {
      toast.error('超商代碼繳費金額不可大於 NT$ 20,000');
      return;
    }
  }

  isSaving.value = true;
  try {
    const result = await createOrder(formData.value);
    
    // 檢查是否有支付表單需要跳轉
    if (result.paymentForm) {
      toast.success('訂單創建成功！正在跳轉到支付頁面...');
      // 創建並提交支付表單
      submitPaymentForm(result.paymentForm);
    } else {
      toast.success('訂單新增成功！');
      handleFormSaved();
    }
  } catch (error) {
    console.error("儲存訂單失敗:", error);
    const errorMessage = error.response?.data?.message || error.message || '操作失敗';
    toast.error(`儲存訂單失敗：${errorMessage}`);
  } finally {
    isSaving.value = false;
  }
};

// 動態創建並提交支付表單
const submitPaymentForm = (paymentFormData) => {
  // 創建表單元素
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = paymentFormData.action;
  form.target = '_blank'; // 在新視窗開啟
  
  // 創建隱藏的輸入欄位
  const fields = [
    { name: 'MerchantID', value: paymentFormData.merchantID },
    { name: 'Version', value: paymentFormData.version },
    { name: 'TradeInfo', value: paymentFormData.tradeInfo },
    { name: 'TradeSha', value: paymentFormData.tradeSha }
  ];
  
  fields.forEach(field => {
    if (field.value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.value;
      form.appendChild(input);
    }
  });
  
  // 將表單添加到頁面並提交
  document.body.appendChild(form);
  form.submit();
  
  // 清理表單元素
  setTimeout(() => {
    if (form.parentNode) {
      form.parentNode.removeChild(form);
    }
  }, 100);
  
  // 提交後關閉當前表單並重新整理訂單列表
  handleFormSaved();
};

const handleFormSaved = async () => {
  isFormVisible.value = false;
  isEditing.value = false;
  editingOrderId.value = null;
  await fetchOrders();
};

const handleFormCancelled = () => {
  isFormVisible.value = false;
  isEditing.value = false;
  editingOrderId.value = null;
};

const addOrderItem = () => {
  formData.value.orderItems.push({
    itemName: '',
    itemDescription: '',
    unitPrice: 0,
    quantity: 1
  });
};

const removeOrderItem = (index) => {
  formData.value.orderItems.splice(index, 1);
};

const viewOrderDetails = async (orderId) => {
  try {
    const order = await getOrderById(orderId);
    console.log('訂單資料:', order);
    
    const itemsHtml = order.orderItems.map((item, idx) => `
      <div style="text-align: left; margin-bottom: 10px; padding: 10px; background: rgba(50,50,60,0.5); border-radius: 5px;">
        <strong>品項 ${idx + 1}:</strong> ${item.itemName}<br>
        ${item.itemDescription ? `描述: ${item.itemDescription}<br>` : ''}
        單價: NT$ ${item.unitPrice} × 數量: ${item.quantity} = NT$ ${item.subtotal || (item.unitPrice * item.quantity)}
      </div>
    `).join('');

    const swalConfig = {
      title: `訂單詳情 - ${order.orderNumber}`,
      customClass: {
        popup: 'swal2-dark-popup',
        title: 'swal2-dark-title',
        htmlContainer: 'swal2-dark-html-container'
      },
      width: '600px',
      html: `
        <div style="text-align: left;">
          <p><strong>客戶姓名:</strong> ${order.customerName}</p>
          <p><strong>訂單日期:</strong> ${order.orderDate || '-'}</p>
          <p><strong>訂單狀態:</strong> ${getStatusText(order.orderStatus)}</p>
          <p><strong>付款方式:</strong> ${getPaymentMethodText(order.paymentMethod)}</p>
          <p><strong>金流平台:</strong> ${order.paymentGateway || ''}</p>
          <p><strong>交易編號:</strong> ${order.tradeNo || ''}</p>
          ${order.paymentExpireDate || order.paymentExpireTime ? `<p><strong>付款期限:</strong> <span style="color: #ff8c69; font-weight: bold;">${order.paymentExpireDate || ''} ${order.paymentExpireTime || ''}</span></p>` : ''}
          ${order.paymentMethod === 'ATM' ? `<p><strong>銀行代碼:</strong> <span style="color: #8be9fd; font-size: 1.1em; font-weight: bold;">${order.bankCode || ''}</span></p>` : ''}
          ${order.paymentMethod === 'ATM' ? `<p><strong>銀行帳號:</strong> <span style="color: #8be9fd; font-size: 1.1em; font-weight: bold;">${order.bankAccount || ''}</span></p>` : ''}
          ${order.paymentMethod === 'ATM' && order.sourceBankId ? `<p><strong>匯款銀行代碼:</strong> <span style="color: #f1fa8c; font-size: 1.0em; font-weight: bold;">${order.sourceBankId || ''}</span></p>` : ''}
          ${order.paymentMethod === 'ATM' && order.sourceAccountNo ? `<p><strong>匯款銀行帳號:</strong> <span style="color: #f1fa8c; font-size: 1.0em; font-weight: bold;">${order.sourceAccountNo || ''}</span></p>` : ''}
          ${order.paymentMethod === 'CONVENIENCE_STORE' ? `<p><strong>超商代碼:</strong> <span style="color: #50fa7b; font-size: 1.2em; font-weight: bold;">${order.convenienceStoreCode || ''}</span></p>` : ''}
          <p><strong>發票載具:</strong> ${getCarrierTypeText(order.carrierType)}${order.carrierId ? ` - ${order.carrierId}` : ''}</p>
          <p><strong>發票號碼:</strong> ${order.invoiceNumber || ''}</p>
          <p><strong>發票狀態:</strong> <span style="color: ${getInvoiceStatusColor(order.invoiceStatus)}; font-weight: bold;">${getInvoiceStatusText(order.invoiceStatus)}</span></p>
          <hr style="margin: 15px 0; border-color: rgba(160,100,220,0.3);">
          <h4>訂單明細:</h4>
          ${itemsHtml}
          <hr style="margin: 15px 0; border-color: rgba(160,100,220,0.3);">
          <p style="font-size: 1.1em;"><strong>訂單總金額: NT$ ${order.totalAmount?.toFixed(2) || '0.00'}</strong></p>
        </div>
      `
    };

    // 如果訂單狀態是PENDING，新增支付按鈕
    if (order.orderStatus === 'PENDING') {
      swalConfig.showCancelButton = true;
      swalConfig.confirmButtonText = '前往支付';
      swalConfig.cancelButtonText = '關閉';
      swalConfig.customClass = {
        ...swalConfig.customClass,
        confirmButton: 'swal2-dark-confirm-button',
        cancelButton: 'swal2-dark-cancel-button'
      };
    }
    // 如果訂單狀態是已付款且發票狀態是PENDING或FAILED，新增開立發票按鈕
    else if (order.orderStatus === 'PAID' && (order.invoiceStatus === 'PENDING' || order.invoiceStatus === 'FAILED')) {
      swalConfig.showCancelButton = true;
      swalConfig.confirmButtonText = '開立發票';
      swalConfig.cancelButtonText = '關閉';
      swalConfig.customClass = {
        ...swalConfig.customClass,
        confirmButton: 'swal2-dark-confirm-button',
        cancelButton: 'swal2-dark-cancel-button'
      };
    }

    const result = await Swal.fire(swalConfig);
    
    // 如果點擊了支付按鈕
    if (result.isConfirmed && order.orderStatus === 'PENDING') {
      await handleGoToPayment(order.orderNumber);
    }
    // 如果點擊了開立發票按鈕
    else if (result.isConfirmed && order.orderStatus === 'PAID' && (order.invoiceStatus === 'PENDING' || order.invoiceStatus === 'FAILED')) {
      await handleCreateInvoice(order.orderNumber);
    }
  } catch (error) {
    console.error("獲取訂單詳情失敗:", error);
    toast.error('獲取訂單詳情失敗');
  }
};


const getStatusText = (status) => {
  const statusMap = {
    'PENDING': '尚未取號',
    'AWAITING_PAYMENT': '等待付款',
    'PAID': '已付款',
    'PAYMENT_FAILED': '付款失敗',
    'SHIPPED': '已出貨',
    'REFUNDED': '已退款'
  };
  return statusMap[status] || status;
};

const getStatusClass = (status) => {
  const classMap = {
    'PENDING': 'status-pending',
    'AWAITING_PAYMENT': 'status-pending',
    'PAID': 'status-paid',
    'PAYMENT_FAILED': 'status-failed',
    'SHIPPED': 'status-shipped',
    'REFUNDED': 'status-refunded'
  };
  return classMap[status] || '';
};

const getCarrierTypeText = (type) => {
  return type === '3J0002' ? '手機條碼' : '無';
};

const getPaymentMethodText = (method) => {
  const methodMap = {
    'ATM': 'ATM轉帳',
    'CONVENIENCE_STORE': '超商代碼'
  };
  return methodMap[method] || method || '-';
};

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
    currentPage.value = page;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleItemsPerPageChange = () => {
  if (currentPage.value === 1) {
    fetchOrders();
  } else {
    currentPage.value = 1;
  }
};

const handleGoToPayment = async (orderNumber) => {
  try {
    const response = await getPendingPaymentOrder(orderNumber);
    
    if (response.paymentForm) {
      toast.success('正在跳轉到支付頁面...');
      submitPaymentForm(response.paymentForm);
    } else {
      toast.error('無法獲取支付資訊');
    }
  } catch (error) {
    console.error("取得支付資訊失敗:", error);
    const errorMessage = error.response?.data?.message || error.message || '操作失敗';
    toast.error(`取得支付資訊失敗：${errorMessage}`);
  }
};

const handleCreateInvoice = async (orderNumber) => {
  try {
    const response = await createInvoiceManually(orderNumber);
    
    // 檢查返回的code是否為"0"，如果不是則視為失敗
    if (response.code === "0") {
      toast.success(`發票開立成功！發票號碼：${response.invoiceNumber || ''}`);
    } else {
      toast.error(`發票開立失敗：${response.message || '未知錯誤'}`);
    }
    
    // 重新載入訂單列表
    await fetchOrders();
  } catch (error) {
    console.error("開立發票失敗:", error);
    const errorMessage = error.response?.data?.message || error.message || '操作失敗';
    toast.error(`開立發票失敗：${errorMessage}`);
  }
};

const getInvoiceStatusText = (status) => {
  const statusMap = {
    'PENDING': '待開立',
    'SUCCESS': '開立成功',
    'FAILED': '開立失敗'
  };
  return statusMap[status] || status || '未知狀態';
};

const getInvoiceStatusColor = (status) => {
  const colorMap = {
    'PENDING': '#ffc107',
    'SUCCESS': '#4caf50',
    'FAILED': '#f44336'
  };
  return colorMap[status] || '#888';
}
</script>

<style scoped>
.order-management-wrapper {
  padding: 20px;
  color: #c0c0c0;
}

.page-breadcrumb {
  font-size: 0.95em;
  color: #888e95;
  margin-bottom: 20px;
}

.page-breadcrumb .separator {
  margin: 0 8px;
  font-size: 0.8em;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 25px;
  align-items: center;
  background-color: rgba(30, 30, 40, 0.7);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.3);
}

.form-input {
  padding: 10px 12px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px;
  color: #e0e0e0;
  font-size: 1em;
  box-sizing: border-box;
  height: 38px;
}

.form-input:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 8px rgba(255, 140, 105, 0.3);
}

.form-input:disabled {
  background-color: rgba(40, 40, 50, 0.5);
  cursor: not-allowed;
}

.search-input {
  flex: 1 1 200px;
}

.search-select {
  flex: 1 1 150px;
  min-width: 120px;
}

.filter-btn, .clear-filter-btn {
  padding: 0 15px;
  height: 38px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  min-width: 90px;
}

.filter-btn {
  background: linear-gradient(145deg, #8be9fd, #50c8ea);
  color: #1a1a1a;
}

.filter-btn:hover {
  background: linear-gradient(145deg, #a0eefe, #60d0f0);
  box-shadow: 0 2px 8px rgba(139, 233, 253, 0.2);
}

.clear-filter-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
}

.clear-filter-btn:hover {
  background-color: rgba(90, 90, 100, 0.8);
}

.actions-bar {
  margin-bottom: 25px;
}

.add-order-btn {
  background: linear-gradient(145deg, #50fa7b, #3ddc67);
  color: #1a1a1a;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.add-order-btn:hover {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
  transform: translateY(-1px);
}

.loading-spinner, .no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.order-table-container {
  overflow-x: auto;
  margin-bottom: 30px;
}

.order-table {
  width: 100%;
  border-collapse: collapse;
  background-color: rgba(35, 35, 45, 0.7);
  border-radius: 8px;
  overflow: hidden;
}

.order-table th {
  background-color: rgba(160, 100, 220, 0.2);
  color: #e0e0e0;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid rgba(160, 100, 220, 0.3);
}

.order-table td {
  padding: 12px;
  border-bottom: 1px solid rgba(160, 100, 220, 0.1);
  color: #c0c0c0;
}

.order-table tr:hover {
  background-color: rgba(160, 100, 220, 0.1);
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 600;
}

.status-pending {
  background-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-paid {
  background-color: rgba(76, 175, 80, 0.2);
  color: #4caf50;
}

.status-shipped {
  background-color: rgba(33, 150, 243, 0.2);
  color: #2196f3;
}

.status-refunded {
  background-color: rgba(244, 67, 54, 0.2);
  color: #f44336;
}

.status-failed {
  background-color: rgba(244, 67, 54, 0.2);
  color: #f44336;
}

.action-cell {
  white-space: nowrap;
}

.action-btn {
  background: none;
  border: 1px solid rgba(160, 100, 220, 0.4);
  color: #adb5bd;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 5px;
  transition: all 0.2s ease;
}

.action-btn.view-btn {
  color: #8be9fd;
  border-color: rgba(139, 233, 253, 0.5);
}

.action-btn.view-btn:hover {
  background-color: rgba(139, 233, 253, 0.15);
}



.order-form-container {
  background-color: rgba(35, 35, 45, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  padding: 20px;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid rgba(160, 100, 220, 0.3);
  padding-bottom: 15px;
}

.form-header h2 {
  color: #e0e0e0;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #ff8080;
  font-size: 1.5em;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
}

.close-btn:hover {
  color: #ff6666;
}

.order-form .form-group {
  margin-bottom: 20px;
}

.order-form label {
  display: block;
  margin-bottom: 8px;
  color: #e0e0e0;
  font-weight: 500;
}

.required {
  color: #ff5555;
}

.order-items-section {
  margin: 30px 0;
  padding: 20px;
  background-color: rgba(25, 25, 35, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.2);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  color: #e0e0e0;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-item-btn {
  background: linear-gradient(145deg, #8be9fd, #50c8ea);
  color: #1a1a1a;
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
}

.add-item-btn:hover {
  background: linear-gradient(145deg, #a0eefe, #60d0f0);
}

.order-item {
  background-color: rgba(40, 40, 50, 0.5);
  border: 1px solid rgba(160, 100, 220, 0.2);
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(160, 100, 220, 0.2);
}

.item-header span {
  color: #8be9fd;
  font-weight: 600;
}

.remove-item-btn {
  background-color: rgba(255, 85, 85, 0.2);
  color: #ff8080;
  border: 1px solid rgba(255, 85, 85, 0.5);
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-item-btn:hover {
  background-color: rgba(255, 85, 85, 0.3);
}

.item-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.total-amount {
  text-align: right;
  font-size: 1.2em;
  color: #50fa7b;
  margin: 20px 0;
  padding: 15px;
  background-color: rgba(80, 250, 123, 0.1);
  border-radius: 5px;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
}

.submit-btn, .cancel-btn {
  padding: 10px 25px;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.submit-btn {
  background: linear-gradient(145deg, #50fa7b, #3ddc67);
  color: #1a1a1a;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
}

.cancel-btn:hover:not(:disabled) {
  background-color: rgba(90, 90, 100, 0.8);
}

.status-note {
  display: block;
  margin-top: 5px;
  font-size: 0.85em;
  color: #888e95;
  font-style: italic;
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
}

.items-per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-dropdown {
  background-color: rgba(40, 40, 50, 0.9);
  color: #e0e0e0;
  border: 1px solid rgba(160, 100, 220, 0.5);
  padding: 6px 10px;
  border-radius: 5px;
}

.page-navigation {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  background-color: rgba(60, 60, 75, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.4);
  color: #adb5bd;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background-color: rgba(160, 100, 220, 0.2);
  color: #f8f9fa;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  padding: 0 10px;
  color: #e0e0e0;
  font-weight: 500;
}

:global(.swal2-dark-popup) {
  background-color: rgba(35, 35, 45, 0.98) !important;
  border: 1px solid rgba(160, 100, 220, 0.6) !important;
  color: #e0e0e0 !important;
}

:global(.swal2-dark-title) {
  color: #ff8c69 !important;
}

:global(.swal2-dark-html-container) {
  color: #c0c0c0 !important;
}

:global(.swal2-dark-confirm-button) {
  background-color: #d33 !important;
  color: #fff !important;
}

:global(.swal2-dark-cancel-button) {
  background-color: rgba(80, 80, 90, 0.7) !important;
  color: #c0c0c0 !important;
}

@media (max-width: 768px) {
  .order-table-container {
    font-size: 0.85em;
  }
  
  .item-fields {
    grid-template-columns: 1fr;
  }
}
</style>